/**
 * KeyboardShortcutsHelp
 *
 * Press `?` (Shift+/) to open a dialog listing every keyboard shortcut
 * registered via `react-hotkeys-hook`. The list is read live from
 * `useHotkeysContext().hotkeys`, so adding a new shortcut anywhere in the
 * app is enough to make it appear here — there is no parallel registry to
 * maintain.
 *
 * Convention for documented shortcuts:
 *   useHotkeys('alt+1', handler, {
 *     description: 'Switch to canvas by position',
 *     metadata: { group: 'Canvas' },
 *   });
 *
 * Shortcuts without a `description` are hidden from this dialog.
 */

import { Fragment, useMemo, useState } from 'react';
import { useHotkeys, useHotkeysContext } from 'react-hotkeys-hook';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Kbd } from './Kbd';

const PRIORITY_GROUPS = ['General', 'Canvas'];
const FALLBACK_GROUP = 'Other';

const isMac = (): boolean =>
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

const KEY_LABELS: Record<string, { mac: string; other: string }> = {
  alt: { mac: '⌥', other: 'Alt' },
  option: { mac: '⌥', other: 'Alt' },
  shift: { mac: '⇧', other: 'Shift' },
  meta: { mac: '⌘', other: 'Ctrl' },
  mod: { mac: '⌘', other: 'Ctrl' },
  ctrl: { mac: '⌃', other: 'Ctrl' },
  control: { mac: '⌃', other: 'Ctrl' },
  enter: { mac: '↩', other: 'Enter' },
  return: { mac: '↩', other: 'Enter' },
  escape: { mac: 'Esc', other: 'Esc' },
  esc: { mac: 'Esc', other: 'Esc' },
  backspace: { mac: '⌫', other: 'Backspace' },
  tab: { mac: '⇥', other: 'Tab' },
  space: { mac: 'Space', other: 'Space' },
  up: { mac: '↑', other: '↑' },
  down: { mac: '↓', other: '↓' },
  left: { mac: '←', other: '←' },
  right: { mac: '→', other: '→' },
  slash: { mac: '/', other: '/' },
};

const formatToken = (raw: string, mac: boolean): string => {
  const lower = raw.toLowerCase().trim();
  const mapped = KEY_LABELS[lower];
  if (mapped) return mac ? mapped.mac : mapped.other;
  if (lower.length === 1) return lower.toUpperCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

interface ShortcutRow {
  description: string;
  hotkeys: string[];
}

interface ShortcutGroup {
  name: string;
  rows: ShortcutRow[];
}

export function KeyboardShortcutsHelp() {
  const [open, setOpen] = useState(false);
  const { hotkeys } = useHotkeysContext();

  useHotkeys(
    'shift+slash',
    (event) => {
      event.preventDefault();
      setOpen((prev) => !prev);
    },
    {
      description: 'Show keyboard shortcuts',
      metadata: { group: 'General' },
      enableOnFormTags: false,
    },
    [],
  );

  const groups = useMemo<ShortcutGroup[]>(() => {
    const byGroup = new Map<string, Map<string, Set<string>>>();
    for (const hk of hotkeys) {
      if (!hk.description) continue;
      const groupName =
        ((hk.metadata as { group?: string } | undefined)?.group) ?? FALLBACK_GROUP;
      const rowsForGroup = byGroup.get(groupName) ?? new Map<string, Set<string>>();
      const keys = rowsForGroup.get(hk.description) ?? new Set<string>();
      keys.add(hk.hotkey);
      rowsForGroup.set(hk.description, keys);
      byGroup.set(groupName, rowsForGroup);
    }

    const ordered: ShortcutGroup[] = [];
    for (const name of PRIORITY_GROUPS) {
      const rows = byGroup.get(name);
      if (!rows) continue;
      ordered.push({ name, rows: rowsToArray(rows) });
      byGroup.delete(name);
    }
    const fallback = byGroup.get(FALLBACK_GROUP);
    byGroup.delete(FALLBACK_GROUP);
    const rest = [...byGroup.entries()].sort(([a], [b]) => a.localeCompare(b));
    for (const [name, rows] of rest) {
      ordered.push({ name, rows: rowsToArray(rows) });
    }
    if (fallback) {
      ordered.push({ name: FALLBACK_GROUP, rows: rowsToArray(fallback) });
    }
    return ordered;
  }, [hotkeys]);

  const mac = isMac();

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Keyboard Shortcuts</DialogTitle>
          <DialogDescription>
            Press <Kbd>{mac ? '⇧' : 'Shift'}</Kbd> <Kbd>/</Kbd> any time to open this dialog.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto -mx-1.5 px-1.5">
          {groups.length === 0 ? (
            <p className="py-4 text-sm text-muted-foreground">
              No shortcuts registered on this view.
            </p>
          ) : (
            <div className="flex flex-col gap-6">
              {groups.map((group) => (
                <section key={group.name} className="flex flex-col gap-2">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {group.name}
                  </h3>
                  <ul className="flex flex-col">
                    {group.rows.map((row) => (
                      <li
                        key={row.description}
                        className="flex items-center justify-between gap-4 py-1.5 text-sm"
                      >
                        <span className="text-foreground">{row.description}</span>
                        <span className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
                          {row.hotkeys.map((hk) => (
                            <ShortcutChip key={hk} hotkey={hk} mac={mac} />
                          ))}
                        </span>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function rowsToArray(rows: Map<string, Set<string>>): ShortcutRow[] {
  return [...rows.entries()]
    .map(([description, keys]) => ({ description, hotkeys: [...keys] }))
    .sort((a, b) => a.description.localeCompare(b.description));
}

function ShortcutChip({ hotkey, mac }: { hotkey: string; mac: boolean }) {
  const tokens = hotkey.split('+').map((t) => formatToken(t, mac));
  return (
    <span className="inline-flex items-center gap-0.5">
      {tokens.map((token, i) => (
        <Fragment key={i}>
          <Kbd>{token}</Kbd>
        </Fragment>
      ))}
    </span>
  );
}

